import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Array, AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import { useTranslation } from '@root/shared/localization'
import { Period } from '@root/shared/period'
import { InfoMessage, SegmentControl } from '@root/shared/ui-kit'

import { reatomPnlChart } from '../../atoms'
import { PnlChartDivision, PnlChartEntry } from '../../model'
import { fromPeriod } from '../../model/pnl-chart-division'

import { PnlInnerChart } from './pnl-inner-chart'

import type { DelugeId } from '@root/entities/user-short'
import type { ClassNameProps } from '@root/shared/react'
import type { ComponentProps } from 'react'

type Props = ClassNameProps & {
  delugeId: DelugeId | undefined
}

export const PnlChartMobile: React.FC<Props> = ({ className, delugeId }) => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomPnlChart({ delugeId }), [delugeId])
  const { t } = useTranslation()

  const [period, setPeriod] = useAtom(model.period)
  const [priceType] = useAtom(model.priceType)
  const [dataState] = useAtom(model)
  const retry = useAction(model.retryAction)

  const lastPnl = dataState.value?.at(-1)?.pnl ?? 0
  // TODO: use performance.realizedPnlUsd instead of lastPnl
  const trend = Trend.of(lastPnl)
  const percents = useMemo(() => {
    return PnlChartEntry.calculatePnlPercentage(dataState.value ?? [])
  }, [dataState.value])

  const currentPeriodLabel = t(PnlChartDivision.PeriodToLabelMap[period])

  return (
    <Root className={className}>
      <Header>
        <HeaderTitle>
          {currentPeriodLabel} {t('portfolio.pnl-chart.title')}
        </HeaderTitle>
        <PnlValue $trend={trend}>
          <Numeric
            value={lastPnl}
            preset="currency-amount-short"
            currency="$"
          />
          {percents ? (
            <span>
              {' '}
              (<Numeric value={percents} preset="percents" />
              %)
            </span>
          ) : undefined}
        </PnlValue>
      </Header>
      {AsyncStateN.matchWaiting(dataState, {
        Error: () => (
          <Loading>
            <InfoMessage
              intent="error"
              variant="dt"
              onRetry={() => retry()}
              retryText={t('common.retry')}
              withSeparator
            />
          </Loading>
        ),
        Waiting: () => (
          <Loading>
            <CircularLoader size={32} />
          </Loading>
        ),
        Success: (data) =>
          Array.isNotEmpty(data) ? (
            <PnlInnerChartMobile
              division={fromPeriod(period)}
              data={data}
              priceType={priceType}
              trend={trend}
            />
          ) : (
            <Loading>
              <InfoMessage intent="empty" variant="dt" />
            </Loading>
          ),
      })}
      <Footer>
        <Control
          value={period}
          onChange={setPeriod}
          css={css`
            grid-column: 3;
          `}
        >
          {Period.PERIODS.map((period) => (
            <SegmentControl.Item key={period} value={period}>
              {t(Period.PERIOD_LABEL_MAP[period]).toUpperCase()}
            </SegmentControl.Item>
          ))}
        </Control>
      </Footer>
    </Root>
  )
}

const Root = styled.div`
  width: 100%;
  background: var(--appearance-1);
  padding: 12px;
  border-radius: 10px;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 10px;

  position: relative;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  justify-content: space-between;
  font-weight: 600;
  gap: 12px;
`
const Footer = styled.div`
  display: flex;
  justify-content: center;
`
const Loading = styled.div`
  display: grid;
  place-items: center;
  aspect-ratio: 350 / 115;
`

const HeaderTitle = styled.div`
  color: var(--text-12);
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
`
const PnlInnerChartMobile = styled(PnlInnerChart)<
  ComponentProps<typeof PnlInnerChart>
>`
  aspect-ratio: 350 / 115;
`

const PnlValue = styled.div<{
  $trend: Trend.Trend
}>`
  color: ${({ $trend }) =>
    Trend.match($trend, {
      Up: () => 'var(--up-9)',
      Down: () => 'var(--down-9)',
      Neutral: () => 'var(--neutral-11)',
    })};
  line-height: normal;
  font-size: 12px;
  font-weight: 600;
`

const Control = styled(SegmentControl)`
  background: var(--backgroundA-2);
` as typeof SegmentControl
